import { Button } from '@1trading/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { FilterEditDialog, type FilterState, TableFilters } from '@/components/filter-bar';
import { ALERT_TABLE_COLUMNS } from '@/features/market-surveillance/alert-columns';
import {
  ALERT_FILTER_CONFIGS,
  ALERT_TIME_RANGE_OPTIONS,
  type AlertTimeRange,
  DEFAULT_ALERT_TIME_RANGE,
  filtersToRequest,
  INITIAL_ALERT_FILTER_STATE,
  timeRangeToRequest,
} from '@/features/market-surveillance/alert-filters';
import { useSurveillanceAlerts } from '@/hooks/queries/market-surveillance/use-surveillance-alerts';
import { getErrorMessage } from '@/lib/api/error';
import type { SurveillanceAlertScope, SurveillanceSortField } from '@/services/api/market-surveillance';

const SORT_OPTIONS: Array<{ field: SurveillanceSortField; label: string }> = [
  { field: 'createdAt', label: 'Newest' },
  { field: 'riskScore', label: 'Highest risk' },
];

// Slack alerts is the working queue (what Compliance already see in Slack); All alerts adds the
// suppressed and low-triage alerts narc stores but never posts
const SCOPE_OPTIONS: Array<{ scope: SurveillanceAlertScope; label: string }> = [
  { scope: 'actionable', label: 'Slack alerts' },
  { scope: 'all', label: 'All alerts' },
];

export const AlertsPanel = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(INITIAL_ALERT_FILTER_STATE);
  const [timeRange, setTimeRange] = useState<AlertTimeRange>(DEFAULT_ALERT_TIME_RANGE);
  const [sort, setSort] = useState<SurveillanceSortField>('createdAt');
  const [scope, setScope] = useState<SurveillanceAlertScope>('actionable');
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [isCustomRangeOpen, setCustomRangeOpen] = useState(false);

  // Preset `from` is frozen per state change (not per render) so the query key — and the
  // keyset-cursor walk minted against it — stays stable while paginating.
  const request = useMemo(() => ({ ...timeRangeToRequest(timeRange), ...filtersToRequest(filters), sort, scope }), [filters, scope, sort, timeRange]);
  const query = useSurveillanceAlerts(request, pagination.cursors[pagination.currentPage]);

  useEffect(() => {
    const nextCursor = query.data?.cursor;
    if (!nextCursor) return;
    setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [query.data]);

  // TableFilters hands back the whole next filter map — replace wholesale and restart the cursor walk.
  // Custom Created At dates and the preset window are mutually exclusive: applying dates parks the
  // preset on 'custom'; clearing them falls back to the bounded default rather than all-history.
  const handleFiltersChange = (next: FilterState) => {
    const hasCustomDates = (next.createdAt ?? []).some(Boolean);
    const hadCustomDates = (filters.createdAt ?? []).some(Boolean);
    if (hasCustomDates) setTimeRange('custom');
    else if (hadCustomDates) setTimeRange(DEFAULT_ALERT_TIME_RANGE);
    setFilters(next);
    setPagination(INITIAL_PAGINATION);
  };

  const handleTimeRangeChange = (range: AlertTimeRange) => {
    if (range === timeRange) return;
    setTimeRange(range);
    setFilters(({ createdAt: _createdAt, ...rest }) => rest);
    setPagination(INITIAL_PAGINATION);
  };

  // Same state path as the Created At chip: apply routes through handleFiltersChange, which
  // parks the preset on 'custom' (or falls back to the default when both dates are cleared).
  const handleCustomRangeApply = (values: string[]) => {
    setCustomRangeOpen(false);
    const { createdAt: _createdAt, ...rest } = filters;
    handleFiltersChange(values.some(Boolean) ? { ...rest, createdAt: values } : rest);
  };

  const handleScopeChange = (next: SurveillanceAlertScope) => {
    if (next === scope) return;
    setScope(next);
    setPagination(INITIAL_PAGINATION);
  };

  // Cursors are minted per sort — switching restarts the walk
  const handleSortChange = (field: SurveillanceSortField) => {
    if (field === sort) return;
    setSort(field);
    setPagination(INITIAL_PAGINATION);
  };

  return (
    <div className="flex flex-col gap-4">
      <TableFilters configs={ALERT_FILTER_CONFIGS} onChange={handleFiltersChange} rows={[]} state={filters} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CursorPagination
          ariaLabel="Alert pages"
          currentPage={pagination.currentPage}
          onPageChange={(page) => setPagination((previous) => ({ ...previous, currentPage: page }))}
          totalPages={pagination.totalPages}
        />
        <div className="flex flex-wrap items-center gap-4">
          <fieldset aria-label="Alert scope" className="m-0 flex items-center gap-1 border-0 p-0">
            {SCOPE_OPTIONS.map((option) => (
              <Button key={option.scope} onClick={() => handleScopeChange(option.scope)} size="sm" type="button" variant={scope === option.scope ? 'primary' : 'outline'}>
                {option.label}
              </Button>
            ))}
          </fieldset>
          <fieldset aria-label="Alert time range" className="m-0 flex items-center gap-1 border-0 p-0">
            {ALERT_TIME_RANGE_OPTIONS.map((option) => (
              <Button key={option.value} onClick={() => handleTimeRangeChange(option.value)} size="sm" type="button" variant={timeRange === option.value ? 'primary' : 'outline'}>
                {option.label}
              </Button>
            ))}
            <Button onClick={() => setCustomRangeOpen(true)} size="sm" type="button" variant={timeRange === 'custom' ? 'primary' : 'outline'}>
              Custom…
            </Button>
          </fieldset>
          <fieldset aria-label="Sort alerts" className="m-0 flex items-center gap-1 border-0 p-0">
            {SORT_OPTIONS.map((option) => (
              <Button key={option.field} onClick={() => handleSortChange(option.field)} size="sm" type="button" variant={sort === option.field ? 'primary' : 'outline'}>
                {option.label}
              </Button>
            ))}
          </fieldset>
        </div>
      </div>
      {isCustomRangeOpen ? (
        <FilterEditDialog
          initial={filters.createdAt ?? []}
          onApply={handleCustomRangeApply}
          onClose={() => setCustomRangeOpen(false)}
          options={[]}
          title="Created At"
          type="date"
        />
      ) : null}
      {query.isError ? (
        <QueryErrorBanner message={`Failed to fetch surveillance alerts: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
      ) : (
        <DataTable
          columns={ALERT_TABLE_COLUMNS}
          data={query.data?.alerts}
          emptyMessage="No alerts match the current filters"
          getRowId={(row) => row.alertId}
          isLoading={query.isFetching}
          onRowClick={(row) => navigate(`/compliance/market-surveillance/${row.alertId}`)}
        />
      )}
    </div>
  );
};
AlertsPanel.displayName = 'AlertsPanel';
